import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import VerificationCodeField from '../../../app/common/fields/VerificationCodeField';
import Button from '../../../app/common/components/Button';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { IReduxRootState } from '../../../app/reduxRootState.interface';
import { sendExistingEmailOtp, validatePassword, verifyEmailOtp } from './verifyEmailFormActions';
import { COLORS, NOTIFICATIONS } from '../../../app/common/constants/styles';
import Icon from '../../../app/common/components/Icon';
import { IChallengeData, languageMap } from '../../../utils/challengerMain';
import { VerifyEmailActionTypes } from './verifyEmailActionTypes';
import { SupportedLanguage } from '../../../config/constants/supportedLanguage.enum';
import { NotificationType } from '../../../app/common/notifications/notificationType.enum';
import SendOtplimitMsg from './SendOtpLimitMsg';
import SendOtpFailedOverlay from './SendOtpFailedOverlay';
import AccountSecurityActions from '../accountSecurityActions';
import { AccountSecurityFormType } from '../AccountSecurity';
import {
    getChallengeData,
    destroyChallenge,
    startChallenge,
    updateChallengerLanguage,
    RenderChallenge,
} from '../../../utils/challengerMain';
import { getEmailFAQUrl, usePrevious } from './verifyEmailUtils';
import { addRatEvent } from '../../../utils/ratClickEvents';

const Wrapper = styled.div`
    line-height: 1.6em;
`;
const PromptWrapper = styled.div`
    margin: 24px auto;
`;
const LabelWrapper = styled.label<{ verifyFailed: boolean }>`
    margin-left: 20px;
    color: ${(props) => (props.verifyFailed ? COLORS.error : '')};
    & > span {
        color: ${(props) => (props.verifyFailed ? COLORS.error : COLORS.grey7)};
    }
`;
const StrongText = styled.strong`
    font-weight: 700;
`;
const ErrorWrapper = styled.div`
    color: ${COLORS.error};
    margin-bottom: 10px;
    margin-left: 23px;
`;
const SendAgainWrapper = styled.div`
    margin: 0px auto 10px 23px;
`;
export const Link = styled.a`
    transition: font-weight 100ms ease-in;
    text-decoration: underline;
    color: ${COLORS.lightBlue};
    cursor: pointer;
    &:hover {
        text-shadow: 0px 0px 1px ${COLORS.grey}, 0px 0px 0.5px ${COLORS.grey};
    }
`;
export const LinkWrapper = styled.div`
    margin: 30px auto;
`;
const SendOtpSuccess = styled.div`
    font-weight: 500;
    color: ${COLORS.green};
    margin-bottom: 10px;
`;
const Sending = styled.div`
    margin-left: 20px;
    margin-bottom: 10px;
    color: #f16a00;
`;
const RemaingOtpExpiration = styled.div`
    margin-left: 20px;
    color: ${COLORS.grey7};
`;
const ButtonWrapper = styled.div`
    margin: 24px auto;
`;
const formatMilliseconds = (ms: number): string => {
    let seconds = Math.floor(ms / 1000);
    let minutes = Math.floor(seconds / 60);
    seconds = seconds % 60;
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
};
const successIcon = NOTIFICATIONS[NotificationType.Success].icon;

const VerifyEmailForm: React.FC = () => {
    const minLength = 6;
    const dispatch = useDispatch();
    const inputId = 'VerifyCode';
    const {
        app: {
            locale,
            appConfig: { EMAIL_OTP_INTERVAL_TIME, CHALLENGER_ENABLED, CHALLENGER_PIDS },
            clientConfig: { emailVerificationFAQUrls },
        },
        accountSecurity: {
            sendOtpTimes,
            sendOtpSuccess,
            verifyFailed,
            tooManySendOtpRequests,
            tooManyVerifyOtpRequests,
            verifyingEmail,
            isVerifyEmailFromUpdateEmail,
            sendingOtp,
        },
    } = useSelector((state: IReduxRootState) => state);

    const helpPageLink = getEmailFAQUrl(emailVerificationFAQUrls, locale) ?? '/';
    const language = languageMap[locale as SupportedLanguage];
    const [remaningOtpIntervalTime, setRemaningOtpIntervalTime] = useState(
        EMAIL_OTP_INTERVAL_TIME ?? 30000,
    );

    const [challengeData, setChallengeData] = useState<IChallengeData>();
    const prevChallengeData = usePrevious(challengeData);
    const { EMAIL_VERIFICATION, ACCOUNT_SECURITY } = CHALLENGER_PIDS;
    const emailVerifyPid = CHALLENGER_ENABLED ? (EMAIL_VERIFICATION as string) : '';
    const accountSecurityPid = CHALLENGER_ENABLED ? (ACCOUNT_SECURITY as string) : '';

    const challengerReadyCallback = useCallback((ready: boolean) => {
        if (ready) {
            setChallengeData(getChallengeData(emailVerifyPid));
        }
    }, []);

    useEffect(() => {
        setRemaningOtpIntervalTime(EMAIL_OTP_INTERVAL_TIME ?? 30000);
        const intervalId = setInterval(() => {
            setRemaningOtpIntervalTime((prev: number) => (prev > 0 ? prev - 1000 : 0));
        }, 1000);
        return () => {
            clearInterval(intervalId);
            destroyChallenge(emailVerifyPid);
            startChallenge(accountSecurityPid);
            dispatch({ type: VerifyEmailActionTypes.DISABLE_VERIFY_EMAIL_FAILURE });
            dispatch({
                type: VerifyEmailActionTypes.TOO_MANY_SEND_OTP_REQUESTS,
                payload: false,
            });
        };
    }, []);
    useEffect(() => {
        if (
            !isVerifyEmailFromUpdateEmail &&
            challengeData &&
            challengeData.cid !== '' &&
            !prevChallengeData
        ) {
            dispatch(
                sendExistingEmailOtp({
                    email: verifyingEmail,
                    language,
                    challengeData,
                    helpPageLink,
                }),
            );
        }
    }, [challengeData]);

    useEffect(() => {
        updateChallengerLanguage(locale);
    }, [locale]);

    const [isCompelete, setIsCompelete] = useState(false);
    const [verificationCode, setVerificationCode] = useState<string>();
    const [verifyTimes, setVerifyTimes] = useState(0);

    const onInputVerifyCode = (code: string) => {
        setVerificationCode(code);
        setIsCompelete(code.length >= minLength);
    };

    const onSubmit = () => {
        if (verificationCode && challengeData) {
            dispatch(
                verifyEmailOtp({
                    email: verifyingEmail,
                    code: verificationCode,
                    challengeData,
                    currentEmail: !isVerifyEmailFromUpdateEmail,
                }),
            );
        } else {
            dispatch({
                type: VerifyEmailActionTypes.VERIFY_EMAIL_FAILURE,
            });
        }
        setVerifyTimes(verifyTimes + 1);
        setIsCompelete(false);
    };

    const sendOtpAgain = () => {
        if (!challengeData) return;
        const payload = {
            email: verifyingEmail as string,
            language,
            challengeData,
            helpPageLink,
        };
        if (isVerifyEmailFromUpdateEmail) {
            dispatch(validatePassword(payload));
        } else {
            dispatch(sendExistingEmailOtp(payload));
        }

        dispatch({ type: VerifyEmailActionTypes.DISABLE_VERIFY_EMAIL_FAILURE });
        setRemaningOtpIntervalTime(EMAIL_OTP_INTERVAL_TIME ?? 30000);
        addRatEvent('email_verification', 'resend_otp');
    };

    const onEditEmail = () => {
        dispatch(
            AccountSecurityActions.changeAccountSecurityOverlay(AccountSecurityFormType.EMAIL),
        );
        dispatch({ type: VerifyEmailActionTypes.CLEAR_OTP_TIMES });
    };

    return (
        <>
            {emailVerifyPid && (
                <RenderChallenge cpid={emailVerifyPid} isReadyCallback={challengerReadyCallback} />
            )}
            {!sendOtpSuccess && !tooManySendOtpRequests ? (
                <SendOtpFailedOverlay
                    sendOtpTimes={sendOtpTimes}
                    email={verifyingEmail}
                    sendOtpAgain={sendOtpAgain}
                />
            ) : (
                <Wrapper>
                    {!tooManySendOtpRequests && (
                        <PromptWrapper>
                            {TranslationHelpers.getFormattedMessage('email.verify.prompt', {
                                verifyingEmail: <StrongText>{verifyingEmail}</StrongText>,
                            })}
                        </PromptWrapper>
                    )}
                    {tooManySendOtpRequests && <SendOtplimitMsg />}
                    <LabelWrapper verifyFailed={verifyFailed} htmlFor={inputId}>
                        {TranslationHelpers.getFormattedMessage('email.verify.code')}
                        <span>
                            {' '}
                            {TranslationHelpers.getFormattedMessage('email.verify.required')}
                        </span>
                    </LabelWrapper>
                    <VerificationCodeField
                        id={inputId}
                        onInputVerifyCode={onInputVerifyCode}
                        verifyFailed={verifyFailed}
                        verifyTimes={verifyTimes}
                    />
                    {verifyFailed && (
                        <ErrorWrapper>
                            <div style={{ display: 'inline-block' }}>
                                {tooManyVerifyOtpRequests ? (
                                    <>
                                        {TranslationHelpers.getFormattedMessage(
                                            'email.verify.exceeded',
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {TranslationHelpers.getFormattedMessage(
                                            'email.verify.codeInvalid',
                                        )}
                                    </>
                                )}
                            </div>
                        </ErrorWrapper>
                    )}
                    {sendOtpTimes > 1 && !tooManySendOtpRequests && !verifyFailed && !sendingOtp && (
                        <>
                            {sendOtpSuccess && (
                                <SendOtpSuccess>
                                    <Icon size="15" icon={successIcon} />
                                    <span style={{ paddingLeft: '8px' }}>
                                        {TranslationHelpers.getFormattedMessage(
                                            'email.verify.sendSuccess',
                                        )}
                                    </span>
                                </SendOtpSuccess>
                            )}
                        </>
                    )}
                    {sendOtpTimes > 1 && sendingOtp && (
                        <Sending>
                            {TranslationHelpers.getFormattedMessage('email.verify.sending')}
                        </Sending>
                    )}
                    {remaningOtpIntervalTime !== 0 && !tooManySendOtpRequests && (
                        <RemaingOtpExpiration>
                            {TranslationHelpers.getFormattedMessage('email.verify.countDownTimer', {
                                time: formatMilliseconds(remaningOtpIntervalTime),
                            })}
                        </RemaingOtpExpiration>
                    )}
                    {remaningOtpIntervalTime === 0 && !tooManySendOtpRequests && sendOtpSuccess && (
                        <SendAgainWrapper>
                            {TranslationHelpers.getFormattedMessage('email.verify.notReceive')}
                            <Link
                                onClick={sendOtpAgain}
                                tabIndex={0}
                                onKeyUp={(e) => {
                                    if (e.keyCode === 13 || e.keyCode === 32) sendOtpAgain();
                                }}
                            >
                                {TranslationHelpers.getFormattedMessage('email.verify.send')}
                            </Link>
                        </SendAgainWrapper>
                    )}
                    <ButtonWrapper>
                        <Button
                            text={TranslationHelpers.getMessageDescriptor('email.verify.button')}
                            primary={true}
                            type="submit"
                            id="submit"
                            loading={false}
                            disabled={!isCompelete}
                            success={false}
                            icon={null}
                            onClick={onSubmit}
                            width={'100%'}
                        />
                    </ButtonWrapper>
                    {isVerifyEmailFromUpdateEmail && (
                        <LinkWrapper>
                            <Link
                                onClick={onEditEmail}
                                tabIndex={0}
                                onKeyUp={(e) => {
                                    if (e.keyCode === 13 || e.keyCode === 32) onEditEmail();
                                }}
                            >
                                {TranslationHelpers.getFormattedMessage('email.verify.editEmail')}
                            </Link>
                        </LinkWrapper>
                    )}
                </Wrapper>
            )}
        </>
    );
};

export default VerifyEmailForm;
